// Shared image/video attachment handling for the product-board report flows —
// the initial report (ReportForm), a reply, and a send-back after review. One
// place for the caps, the validation messages and the base64 encoding, so a
// screenshot behaves identically wherever it's attached.
//
// Files ride as base64 JSON to the server (tickets.js parses those routes with a
// 60mb limit), so the TOTAL cap has to stay comfortably under that after the
// ~1.37× base64 overhead.
export const MAX_FILES = 4;
export const MAX_MB = 20;        // per file
export const MAX_TOTAL_MB = 40;  // all files together

// Read a file/blob straight to a data-URL (used for video, kept as-is).
export function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Downscale an image to a small JPEG data-URL before upload (keeps the disk sane
// and the upload fast on a phone).
export function downscaleImage(file, max = 1600, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject; img.src = reader.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

// Turn picked File objects into attachment records, validating type/size/count
// against what's already attached. Returns { files, error } — `files` is the new
// full list (existing + accepted) and `error` the first clear failure message, so
// a caller can show one message rather than swallowing a rejected screenshot.
// `images` = images only (reply/send-back flows); false also allows video.
export async function addPickedFiles(picked, existing, { images = false, max = MAX_FILES } = {}) {
  let files = Array.isArray(existing) ? [...existing] : [];
  let error = '';
  for (const f of Array.from(picked || [])) {
    if (files.length >= max) { error = `Up to ${max} file${max === 1 ? '' : 's'}.`; break; }
    const isImage = (f.type || '').startsWith('image/');
    const isVideo = (f.type || '').startsWith('video/');
    if (!isImage && (images || !isVideo)) { error = images ? 'Only images (PNG, JPG…) can be attached.' : 'Only images and videos can be attached.'; continue; }
    if (f.size > MAX_MB * 1024 * 1024) { error = `Each file must be under ${MAX_MB}MB.`; continue; }
    let data;
    try { data = isImage ? await downscaleImage(f) : await readAsDataURL(f); }
    catch { error = 'Could not read that file.'; continue; }
    const total = files.reduce((n, x) => n + (x.data?.length || 0), 0) + data.length;
    if (total > MAX_TOTAL_MB * 1024 * 1024 * 1.37) { error = `Attachments together must stay under ${MAX_TOTAL_MB}MB — remove one first.`; continue; }
    files = [...files, { name: f.name || (isImage ? 'screenshot.jpg' : 'clip'), mime: isImage ? 'image/jpeg' : f.type, data, isImage }];
  }
  return { files, error };
}

// The wire shape the ticket routes expect (drops the local `isImage` hint).
export const wire = (files) => (files || []).map((f) => ({ name: f.name, mime: f.mime, data: f.data }));
